import { useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  Plus,
  Trash2,
  Info,
  Calendar,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useApi from '../../api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatCalendarOptionLabel } from '../../../lib/calendarIntegrationLabel';
import ConditionBuilder from '../builder/ConditionBuilder';
import MentionEditor from '../builder/MentionEditor';
import { buildMentionables, Mentionable } from '../builder/promptMentions';
import useObjectiveMentions from '../builder/useObjectiveMentions';
import { buildWorkflowConditionFields } from '../builder/workflowConditionFields';
import { WorkflowConditionField } from '../builder/workflowConditionFields';
import { useLifecycleStages } from '../../accounts/lifecycleStages';
import {
  AgentSkill,
  AgentSkillSurface,
  SkillEscalationConfig,
  SkillEscalationRep,
  SkillsTabProps,
  isConditionalSkillMissingRules,
} from './types';
import AgentTabHeader from './AgentTabHeader';
import { mcpSkillToolOptions, McpSkillToolOption } from './mcpTools';
import { useMcpServers } from '../../integrations/mcpServerRegistry';
import { crmSkillToolOptions, CrmSkillToolOption } from './crmTools';

// The canonical tools the agent can use. Descriptions are platform-owned and
// immutable — skills decide WHEN a tool is used, never HOW it works. Ambient
// tools are always available; grantable tools become available only while a
// skill that grants them is active.
interface AgentToolInfo {
  name: string;
  label: string;
  description: string;
  grantable: boolean;
}

const AGENT_TOOLS: AgentToolInfo[] = [
  {
    name: 'update_form',
    label: 'Update form',
    description: 'Saves information the visitor shares into the fields defined in Data Capture.',
    grantable: false,
  },
  {
    name: 'ask_question',
    label: 'Ask question',
    description: 'Presents interactive question cards the visitor can answer with a click instead of typing.',
    grantable: false,
  },
  {
    name: 'search_knowledge',
    label: 'Search knowledge',
    description: "Searches the agent's knowledge base to answer the visitor's questions.",
    grantable: false,
  },
  {
    name: 'show_booking_calendar',
    label: 'Show booking calendar',
    description: 'Displays a scheduling calendar in the chat so the visitor can book a meeting. Each skill that grants this tool chooses which calendar it shows.',
    grantable: true,
  },
  {
    name: 'escalate_to_human',
    label: 'Escalate to human',
    description: 'Hands the conversation to a human teammate and notifies them.',
    grantable: true,
  },
  {
    name: 'web_search',
    label: 'Web search',
    description: 'Searches the public web for current information and can fetch selected result pages for more detail.',
    grantable: true,
  },
];

// Tools a skill can grant while active; ambient tools are intentionally absent.
const SKILL_TOOL_OPTIONS = AGENT_TOOLS.filter((tool) => tool.grantable);

const SKILL_SURFACE_OPTIONS: Array<{ value: AgentSkillSurface; label: string; description: string }> = [
  {
    value: 'website',
    label: 'Website',
    description: 'Available in the public website chat experience.',
  },
  {
    value: 'app',
    label: 'App',
    description: 'Available when the agent is running inside the customer app.',
  },
];

const ALWAYS_ON = { mode: 'always' as const, conditions: [], formula: '' };

function newCustomSkill(overrides: Partial<AgentSkill> = {}): AgentSkill {
  return {
    id: `skill_${Math.random().toString(36).slice(2, 10)}`,
    name: '',
    description: '',
    surfaces: ['website'],
    enabled: true,
    activation: { ...ALWAYS_ON },
    instructions: '',
    tools: [],
    ...overrides,
  };
}

function normalizeSkillSurfaces(skill: AgentSkill): AgentSkillSurface[] {
  return Array.isArray(skill.surfaces) ? skill.surfaces : [];
}

interface CalendarOption {
  id: string;
  name: string;
  details: Record<string, any>;
  displayName: string;
}

interface RepOption {
  id: string;
  email: string;
  name: string;
}

const OWNER_ROLE_OPTIONS: Array<{
  value: SkillEscalationConfig['account_owner_role'];
  label: string;
  description: string;
}> = [
  {
    value: 'assignee',
    label: 'Assign the conversation to them',
    description: 'The owner takes matched escalations; routing below covers everyone else.',
  },
  {
    value: 'notify',
    label: 'Notify them, assign via routing',
    description: 'Routing below picks the assignee; the owner gets an FYI mention.',
  },
  {
    value: 'context',
    label: 'Show as context only',
    description: 'The owner appears by name in notifications, without a ping.',
  },
];

function normalizeEscalationConfig(skill: AgentSkill): SkillEscalationConfig {
  const raw = skill.escalation_config;
  return {
    assignment: raw?.assignment === 'round_robin' ? 'round_robin' : 'none',
    account_owner_role:
      raw?.account_owner_role === 'assignee' || raw?.account_owner_role === 'context'
        ? raw.account_owner_role
        : 'notify',
    reps: Array.isArray(raw?.reps) ? raw!.reps.filter((rep) => rep && rep.id) : [],
  };
}

interface SkillRowProps {
  skill: AgentSkill;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (updated: AgentSkill) => void;
  onDelete: () => void;
  mentionables: Mentionable[];
  conditionFields: WorkflowConditionField[];
  calendarOptions: CalendarOption[];
  repOptions: RepOption[];
  mcpToolOptions: McpSkillToolOption[];
  crmToolOptions: CrmSkillToolOption[];
}

export function SkillRow({
  skill,
  isExpanded,
  onToggleExpand,
  onChange,
  onDelete,
  mentionables,
  conditionFields,
  calendarOptions,
  repOptions,
  mcpToolOptions,
  crmToolOptions,
}: SkillRowProps) {
  const grantsCalendar = (skill.tools || []).includes('show_booking_calendar');
  const grantsEscalation = (skill.tools || []).includes('escalate_to_human');
  const escalationConfig = normalizeEscalationConfig(skill);
  const isRoundRobin = escalationConfig.assignment === 'round_robin';

  const setEscalationConfig = (patch: Partial<SkillEscalationConfig>) =>
    onChange({ ...skill, escalation_config: { ...escalationConfig, ...patch } });

  const repSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleRepDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const reps = escalationConfig.reps;
    const oldIndex = reps.findIndex((rep) => rep.id === active.id);
    const newIndex = reps.findIndex((rep) => rep.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setEscalationConfig({ reps: arrayMove(reps, oldIndex, newIndex) });
  };

  const availableRepOptions = repOptions.filter(
    (option) => !escalationConfig.reps.some((rep) => rep.id === option.id)
  );
  const isConditional = skill.activation?.mode === 'conditional';
  const missingActivationRules = isConditionalSkillMissingRules(skill);
  const selectedSurfaces = normalizeSkillSurfaces(skill);

  const setActivation = (patch: Partial<AgentSkill['activation']>) =>
    onChange({ ...skill, activation: { ...skill.activation, ...patch } });

  const setSurface = (surface: AgentSkillSurface, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...selectedSurfaces, surface]))
      : selectedSurfaces.filter((value) => value !== surface);
    if (next.length === 0) return;
    onChange({ ...skill, surfaces: next });
  };

  const calendarConfiguration = (
    <div className="space-y-2 border-t bg-muted/10 p-3">
      <Label>Calendar</Label>
      <p className="text-xs text-muted-foreground">
        The calendar this skill shows when the AI books a meeting.
      </p>
      <Select
        value={skill.calendar_config?.id || ''}
        onValueChange={(value) => {
          const option = calendarOptions.find((o) => o.id === value);
          if (option) {
            onChange({
              ...skill,
              calendar_config: {
                id: option.id,
                name: option.name,
                details: option.details || {},
              },
            });
          }
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select a calendar..." />
        </SelectTrigger>
        <SelectContent>
          {skill.calendar_config &&
            !calendarOptions.some((o) => o.id === skill.calendar_config?.id) && (
              <SelectItem value={skill.calendar_config.id}>
                {formatCalendarOptionLabel({
                  providerName: skill.calendar_config.name,
                  details: skill.calendar_config.details,
                })} (saved)
              </SelectItem>
            )}
          {calendarOptions.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!skill.calendar_config && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This skill stays inactive until a calendar is selected.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const escalationConfiguration = (
    <div className="space-y-4 border-t bg-muted/10 p-3">
      <div className="space-y-2">
        <Label>Escalation routing</Label>
        <p className="text-xs text-muted-foreground">
          How escalations from this skill are assigned to your team.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setEscalationConfig({ assignment: 'none' })}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition-colors',
              !isRoundRobin
                ? 'border-foreground/20 bg-muted/30'
                : 'border-input hover:border-foreground/30 hover:bg-muted/50'
            )}
          >
            <span className="text-sm font-medium">Notify everyone</span>
            <span className="text-xs text-muted-foreground">First come, first serve</span>
          </button>
          <button
            type="button"
            onClick={() => setEscalationConfig({ assignment: 'round_robin' })}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition-colors',
              isRoundRobin
                ? 'border-foreground/20 bg-muted/30'
                : 'border-input hover:border-foreground/30 hover:bg-muted/50'
            )}
          >
            <span className="text-sm font-medium">Round-robin</span>
            <span className="text-xs text-muted-foreground">Rotate through a rep pool</span>
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Rep pool</Label>
        <p className="text-xs text-muted-foreground">
          {isRoundRobin
            ? 'Escalations rotate through these reps in order, skipping anyone outside their work hours.'
            : 'Only these reps are notified and counted for availability. Leave empty to notify everyone with escalation notifications on.'}
        </p>
        {escalationConfig.reps.length > 0 && (
          <DndContext sensors={repSensors} collisionDetection={closestCenter} onDragEnd={handleRepDragEnd}>
            <SortableContext
              items={escalationConfig.reps.map((rep) => rep.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1.5">
                {escalationConfig.reps.map((rep, index) => (
                  <SortableRepRow
                    key={rep.id}
                    rep={rep}
                    index={index}
                    isStale={!repOptions.some((option) => option.id === rep.id)}
                    onRemove={() =>
                      setEscalationConfig({
                        reps: escalationConfig.reps.filter((entry) => entry.id !== rep.id),
                      })
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        <Select
          value=""
          onValueChange={(value) => {
            const option = repOptions.find((o) => o.id === value);
            if (option) {
              setEscalationConfig({
                reps: [
                  ...escalationConfig.reps,
                  { id: option.id, email: option.email, name: option.name },
                ],
              });
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Add a rep..." />
          </SelectTrigger>
          <SelectContent>
            {availableRepOptions.length === 0 ? (
              <SelectItem value="__none__" disabled>
                No more teammates to add
              </SelectItem>
            ) : (
              availableRepOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {isRoundRobin && escalationConfig.reps.length === 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Add at least one rep — round-robin without a pool falls back to notifying everyone
              with escalation notifications on.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="space-y-2">
        <Label>When a CRM account owner is matched</Label>
        <p className="text-xs text-muted-foreground">
          Applies when a visitor&apos;s company matches an account in your connected CRM.
        </p>
        <Select
          value={escalationConfig.account_owner_role}
          onValueChange={(value) =>
            setEscalationConfig({
              account_owner_role: value as SkillEscalationConfig['account_owner_role'],
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OWNER_ROLE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {OWNER_ROLE_OPTIONS.find((option) => option.value === escalationConfig.account_owner_role)?.description}
        </p>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border bg-card transition-colors',
        isExpanded ? 'border-foreground/20' : 'hover:border-foreground/20'
      )}
    >
      <Collapsible open={isExpanded} onOpenChange={onToggleExpand}>
        <CollapsibleTrigger className="flex items-center gap-3 w-full p-4 hover:bg-muted/50 transition-colors">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}

          <div className="flex-1 min-w-0 text-left">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className={cn('min-w-0 max-w-full truncate text-sm font-medium', !skill.name && 'text-muted-foreground italic')}>
                {skill.name || 'Unnamed skill'}
              </span>
              <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                {isConditional ? 'Conditional' : 'Always on'}
              </Badge>
              {missingActivationRules && (
                <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 text-muted-foreground">
                  Needs rules
                </Badge>
              )}
              {grantsCalendar &&
                (skill.calendar_config ? (
                  <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 gap-1 text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {formatCalendarOptionLabel({
                      providerName: skill.calendar_config.name,
                      details: skill.calendar_config.details,
                    })}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 gap-1 text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    Needs calendar
                  </Badge>
                ))}
              {grantsEscalation && isRoundRobin && (
                <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 gap-1 text-muted-foreground">
                  <Users className="h-3 w-3" />
                  {escalationConfig.reps.length > 0
                    ? `Round-robin · ${escalationConfig.reps.length} rep${escalationConfig.reps.length !== 1 ? 's' : ''}`
                    : 'Needs reps'}
                </Badge>
              )}
            </div>
          </div>

          {/* Stop propagation so toggling/deleting doesn't expand the row. */}
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={skill.enabled}
              onCheckedChange={(checked) => onChange({ ...skill, enabled: checked })}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-muted-foreground hover:text-destructive [&_svg]:size-3.5"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-1 space-y-5 border-t">
            {/* Name */}
            <div className="space-y-2 pt-4">
              <Label htmlFor={`skill-name-${skill.id}`}>Name</Label>
              <Input
                id={`skill-name-${skill.id}`}
                value={skill.name}
                placeholder="e.g. Handle pricing objections"
                onChange={(e) => onChange({ ...skill, name: e.target.value })}
              />
            </div>

            {isConditional && (
              <div className="space-y-2">
                <Label>Inactive summary</Label>
                <p className="text-xs text-muted-foreground">
                  One sentence the AI can see before this skill is active. Describe what becomes possible, not what to do.
                </p>
                <Textarea
                  value={skill.description || ''}
                  placeholder="e.g. Can schedule a sales meeting once the visitor is qualified."
                  className="min-h-[60px]"
                  onChange={(event) => onChange({ ...skill, description: event.target.value })}
                />
              </div>
            )}

            {/* Surfaces */}
            <div className="space-y-2">
              <Label>Available in</Label>
              <div className="grid grid-cols-1 @lg:grid-cols-2 gap-2">
                {SKILL_SURFACE_OPTIONS.map((surface) => {
                  const checked = selectedSurfaces.includes(surface.value);
                  return (
                    <label
                      key={surface.value}
                      className="flex items-start gap-2 p-2 border rounded-md cursor-pointer hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={checked}
                        className="mt-0.5"
                        onCheckedChange={(value) => setSurface(surface.value, Boolean(value))}
                      />
                      <div>
                        <span className="text-sm">{surface.label}</span>
                        <p className="text-xs text-muted-foreground">{surface.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Activation — segmented choice */}
            <div className="space-y-3">
              <Label>When is this skill active?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setActivation({ mode: 'always' })}
                  className={cn(
                    'flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition-colors',
                    !isConditional
                      ? 'border-foreground/20 bg-muted/30'
                      : 'border-input hover:border-foreground/30 hover:bg-muted/50'
                  )}
                >
                  <span className="text-sm font-medium">Always active</span>
                  <span className="text-xs text-muted-foreground">In every conversation</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActivation({ mode: 'conditional' })}
                  className={cn(
                    'flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition-colors',
                    isConditional
                      ? 'border-foreground/20 bg-muted/30'
                      : 'border-input hover:border-foreground/30 hover:bg-muted/50'
                  )}
                >
                  <span className="text-sm font-medium">Conditional</span>
                  <span className="text-xs text-muted-foreground">Only when rules match</span>
                </button>
              </div>

              {isConditional && (
                <div className="space-y-3">
                  <div className="rounded-lg border p-3">
                    <ConditionBuilder
                      conditions={skill.activation.conditions}
                      setConditions={(conditions) => setActivation({ conditions })}
                      formula={skill.activation.formula}
                      setFormula={(formula) => setActivation({ formula })}
                      conditionFields={conditionFields}
                    />
                  </div>
                  {missingActivationRules && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Add at least one condition, or switch back to Always active — conditional
                        skills with no rules never activate.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="space-y-2">
              <Label>Instructions</Label>
              <MentionEditor
                value={skill.instructions}
                mentionables={mentionables}
                placeholder="Explain what the AI should do while this skill is active... Type @ to reference fields, skills, or tools."
                onChange={(instructions) => onChange({ ...skill, instructions })}
              />
            </div>

            {/* Tools */}
            <div className="space-y-2">
              <Label>Tools</Label>
              <div className="space-y-2">
                {SKILL_TOOL_OPTIONS.map((tool) => {
                  const checked = (skill.tools || []).includes(tool.name);
                  return (
                    <div
                      key={tool.name}
                      className="overflow-hidden rounded-md border"
                    >
                      <label className="flex cursor-pointer items-start gap-2 p-2 hover:bg-muted/40">
                        <Checkbox
                          checked={checked}
                          className="mt-0.5"
                          onCheckedChange={(value) =>
                            onChange({
                              ...skill,
                              tools: value
                                ? [...(skill.tools || []), tool.name]
                                : (skill.tools || []).filter((name) => name !== tool.name),
                            })
                          }
                        />
                        <div>
                          <span className="text-sm">{tool.label}</span>
                          <p className="text-xs text-muted-foreground">{tool.description}</p>
                        </div>
                      </label>
                      {checked && tool.name === 'show_booking_calendar' && calendarConfiguration}
                      {checked && tool.name === 'escalate_to_human' && escalationConfiguration}
                    </div>
                  );
                })}

                {mcpToolOptions.length > 0 && (
                  <>
                    <p className="pt-1 text-xs font-medium text-muted-foreground">
                      MCP server tools
                    </p>
                    <p className="text-xs text-muted-foreground">
                      MCP tools are available all the time by default. Adding one to a
                      skill scopes it: it then works only while a skill that includes it
                      is active.
                    </p>
                    <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                      {mcpToolOptions.map((option) => {
                        const checked = (skill.tools || []).includes(option.name);
                        return (
                          <label
                            key={option.name}
                            className="flex cursor-pointer items-start gap-2 rounded-md border p-2 hover:bg-muted/40"
                          >
                            <Checkbox
                              checked={checked}
                              className="mt-0.5"
                              onCheckedChange={(value) =>
                                onChange({
                                  ...skill,
                                  tools: value
                                    ? [...(skill.tools || []), option.name]
                                    : (skill.tools || []).filter((name) => name !== option.name),
                                })
                              }
                            />
                            <div className="min-w-0">
                              <span className="text-sm">{option.label}</span>
                              {option.description && (
                                <p
                                  className="text-xs text-muted-foreground line-clamp-2"
                                  title={option.description}
                                >
                                  {option.description}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}

                {crmToolOptions.length > 0 && (
                  <>
                    <p className="pt-1 text-xs font-medium text-muted-foreground">
                      CRM tools
                    </p>
                    <p className="text-xs text-muted-foreground">
                      CRM tools work only for identity-verified visitors and are
                      available by default once enabled in the CRM mapping. Adding
                      one to a skill scopes it: it then works only while a skill
                      that includes it is active.
                    </p>
                    <div className="space-y-2">
                      {crmToolOptions.map((option) => {
                        const checked = (skill.tools || []).includes(option.name);
                        return (
                          <label
                            key={option.name}
                            className="flex cursor-pointer items-start gap-2 rounded-md border p-2 hover:bg-muted/40"
                          >
                            <Checkbox
                              checked={checked}
                              className="mt-0.5"
                              onCheckedChange={(value) =>
                                onChange({
                                  ...skill,
                                  tools: value
                                    ? [...(skill.tools || []), option.name]
                                    : (skill.tools || []).filter((name) => name !== option.name),
                                })
                              }
                            />
                            <div className="min-w-0">
                              <span className="text-sm">{option.label}</span>
                              <p className="text-xs text-muted-foreground">
                                {option.description}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function SortableRepRow({
  rep,
  index,
  isStale,
  onRemove,
}: {
  rep: SkillEscalationRep;
  index: number;
  isStale: boolean;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rep.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-card px-2 py-1.5',
        isDragging && 'shadow-lg ring-2 ring-foreground/20'
      )}
    >
      <div {...attributes} {...listeners} className="cursor-move p-0.5 rounded hover:bg-muted">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <span className="w-5 shrink-0 text-xs text-muted-foreground tabular-nums">{index + 1}.</span>
      <div className="flex-1 min-w-0">
        <span className="block truncate text-sm">{rep.name || rep.email}</span>
      </div>
      {isStale && (
        <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 text-muted-foreground">
          Not in workspace
        </Badge>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-5 w-5 text-muted-foreground hover:text-destructive [&_svg]:size-3.5"
        onClick={onRemove}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function SkillsTab({
  agId,
  form,
  skills,
  setSkills,
  enableSkills,
  setEnableSkills,
  enableTracks,
  actions,
  setNeedsUpdate,
}: SkillsTabProps) {
  const [expandedSkillId, setExpandedSkillId] = useState<string | null>(null);
  const [calendarIntegrations, setCalendarIntegrations] = useState<any[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);
  const [calendarsFetched, setCalendarsFetched] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);

  const { get } = useApi();

  const { stages: lifecycleStages } = useLifecycleStages();
  const conditionFields = useMemo(
    () =>
      buildWorkflowConditionFields(form || [], {
        lifecycleStageKeys: lifecycleStages.map((stage) => stage.key),
      }),
    [form, lifecycleStages]
  );
  // Bindings reference org MCP servers; the catalog comes from the registry.
  const { servers: mcpServers } = useMcpServers();
  const mcpToolOptions = useMemo(
    () => mcpSkillToolOptions(actions || {}, mcpServers),
    [actions, mcpServers]
  );
  const crmToolOptions = useMemo(() => crmSkillToolOptions(actions || {}), [actions]);
  const objectiveMentions = useObjectiveMentions(agId);
  const mentionables = useMemo(
    () =>
      buildMentionables(form || [], skills || [], {
        excludeSkillId: expandedSkillId || undefined,
        users: organizationUsers,
        objectives: objectiveMentions,
      }),
    [form, skills, expandedSkillId, organizationUsers, objectiveMentions]
  );

  const expandedSkill = (skills || []).find((s) => s.id === expandedSkillId);
  const expandedGrantsCalendar = Boolean(expandedSkill?.tools?.includes('show_booking_calendar'));
  const expandedGrantsEscalation = Boolean(expandedSkill?.tools?.includes('escalate_to_human'));

  // Fetch selectable calendars (same source as track terminal actions) the
  // first time a calendar-granting skill is expanded.
  useEffect(() => {
    if (!expandedGrantsCalendar || calendarsFetched) return;
    setCalendarsFetched(true);
    const fetchCalendars = async () => {
      try {
        const [integrationsResponse, usersResponse] = await Promise.all([
          get('/integrations/?include_all_calendars=true'),
          get('/users/'),
        ]);
        const calendarApps = ['calendly', 'tidycal', 'cal-com', 'hubspot-meetings', 'chili-piper', 'default', 'revenue-hero'];
        setCalendarIntegrations(
          integrationsResponse.data.filter((integration: any) =>
            calendarApps.includes(integration.name) &&
            (integration.user_id != null ||
              ['chili-piper', 'default', 'revenue-hero'].includes(integration.name))
          )
        );
        setOrganizationUsers(usersResponse.data || []);
        setUsersFetched(true);
      } catch (error) {
        console.error('Error fetching calendar integrations:', error);
      }
    };
    fetchCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedGrantsCalendar, calendarsFetched]);

  // Fetch org users the first time any skill is expanded (unless already
  // loaded above): the escalation rep pool needs them, and the instructions
  // editor offers them as Team @mentions.
  useEffect(() => {
    if (!expandedSkillId || usersFetched) return;
    setUsersFetched(true);
    const fetchUsers = async () => {
      try {
        const usersResponse = await get('/users/');
        setOrganizationUsers(usersResponse.data || []);
      } catch (error) {
        console.error('Error fetching organization users:', error);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSkillId, usersFetched]);

  const calendarOptions = useMemo<CalendarOption[]>(() => {
    return calendarIntegrations.map((integration) => {
      const user = integration.user_id
        ? organizationUsers.find((u: any) => u.id === integration.user_id)
        : null;
      const userName = user
        ? (user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.first_name || user.last_name || user.email)
        : null;
      return {
        id: integration.id,
        name: integration.name,
        details: integration.details,
        displayName: formatCalendarOptionLabel({
          providerName: integration.name,
          userName,
          details: integration.details,
        }),
      };
    });
  }, [calendarIntegrations, organizationUsers]);

  const repOptions = useMemo<RepOption[]>(() => {
    return (organizationUsers || [])
      .filter((user: any) => user?.id && user?.email && user?.is_active !== false)
      .map((user: any) => ({
        id: String(user.id),
        email: user.email,
        name:
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : user.first_name || user.last_name || user.email,
      }));
  }, [organizationUsers]);

  // One-time migrations, persisted on the next save:
  // 1. Legacy drafts may hold "system" skills from the pre-launch design.
  //    Those were tool wrappers, not skills — drop them, except escalation,
  //    whose instructions are customer prose: it converts to a normal skill.
  // 2. First-time setup: import the legacy escalation prompt (the plain
  //    path's tools.escalations.prompt) as a starter skill. Scheduling gets
  //    no counterpart on purpose — its legacy config is pure mechanics with
  //    no customer prose to convert.
  useEffect(() => {
    const existing = skills || [];
    const hasLegacySystemSkills = existing.some((skill) => skill.type === 'system');
    const escalationPrompt = actions?.escalations?.prompt || '';

    if (hasLegacySystemSkills) {
      setSkills(
        existing
          .filter((skill) => skill.type !== 'system' || skill.id === 'escalation')
          .map(({ type, ...skill }) => skill)
      );
      return;
    }

    if (existing.length === 0 && escalationPrompt) {
      setSkills([
        newCustomSkill({
          name: 'Escalate to Human',
          description: 'Can connect the visitor with a human teammate.',
          // The legacy prompt served both surfaces, so the import does too —
          // enabling skills must not silently drop app-mode escalation.
          surfaces: ['website', 'app'],
          instructions: escalationPrompt,
          tools: ['escalate_to_human'],
        }),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (value: boolean) => {
    if (value && enableTracks) {
      toast.error(
        'Disable the Visitor Workflow first — Skills and Workflow cannot run at the same time.',
        toastConfig
      );
      return;
    }
    setEnableSkills(value);
    setNeedsUpdate(true);
  };

  // Live edits update state only; the agent-level Save Draft / Publish
  // persists them (calling setNeedsUpdate here would auto-save every keystroke).
  const updateSkill = (updated: AgentSkill) => {
    setSkills(skills.map((skill) => (skill.id === updated.id ? updated : skill)));
  };

  const deleteSkill = (id: string) => {
    setSkills(skills.filter((skill) => skill.id !== id));
    if (expandedSkillId === id) setExpandedSkillId(null);
  };

  const createSkill = () => {
    const skill = newCustomSkill();
    setSkills([...(skills || []), skill]);
    setExpandedSkillId(skill.id);
  };

  // A connected calendar with no skill granting the booking tool means the
  // AI can't schedule — surface that instead of letting it fail silently.
  const calendarConfigured = Boolean(actions?.scheduling?.name || actions?.scheduling?.id);
  const calendarGranted = (skills || []).some(
    (skill) => skill.enabled && (skill.tools || []).includes('show_booking_calendar')
  );
  const skillsMissingActivationRules = (skills || []).filter(isConditionalSkillMissingRules);

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={Sparkles}
        title="Skills"
        description="Define instructions and tools that are available always or when conditions match."
        actions={(
          <div className="flex items-center gap-2">
            <Label htmlFor="skills-toggle" className="cursor-pointer text-xs text-muted-foreground">
              {enableSkills ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch id="skills-toggle" checked={enableSkills} onCheckedChange={handleToggle} />
          </div>
        )}
      />

      <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
      {enableTracks && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Visitor Workflow is enabled.</strong> Skills and the workflow cannot run at the
            same time — disable the workflow in the Workflow tab to use skills.
          </AlertDescription>
        </Alert>
      )}

      {enableSkills && calendarConfigured && !calendarGranted && (
        <Alert>
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            A calendar is connected, but no skill uses the booking calendar tool yet — the AI
            can&apos;t schedule meetings until one does. Create a skill that grants{' '}
            <strong>Show booking calendar</strong>.
          </AlertDescription>
        </Alert>
      )}

      {enableSkills && skillsMissingActivationRules.length > 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {skillsMissingActivationRules.length === 1 ? (
              <>
                <strong>{skillsMissingActivationRules[0].name || 'Unnamed skill'}</strong> is
                conditional but has no rules — add conditions or switch it to Always active before
                saving.
              </>
            ) : (
              <>
                {skillsMissingActivationRules.length} skills are conditional with no rules — add
                conditions or switch them to Always active before saving.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Skill list */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">Agent skills</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Create reusable behavior for specific situations.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={createSkill}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Create skill
          </Button>
        </div>

        {(skills || []).length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
            <p className="text-sm font-medium">No skills yet</p>
            <p className="text-xs text-muted-foreground max-w-lg mx-auto">
              Skills are instructions the AI follows — always, or only when conditions match. Think
              &quot;Qualify Visitor&quot; or &quot;Handle Pricing Objections&quot;: describe in plain
              language what to do and when, and type @ to reference your form fields — e.g.
              &quot;Before offering to book a demo, collect @Email and @Company.&quot;
            </p>
            <Button type="button" variant="outline" size="sm" onClick={createSkill}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Create your first skill
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {(skills || []).map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                isExpanded={expandedSkillId === skill.id}
                onToggleExpand={() =>
                  setExpandedSkillId(expandedSkillId === skill.id ? null : skill.id)
                }
                onChange={updateSkill}
                onDelete={() => deleteSkill(skill.id)}
                mentionables={mentionables}
                conditionFields={conditionFields}
                calendarOptions={calendarOptions}
                repOptions={repOptions}
                mcpToolOptions={mcpToolOptions}
                crmToolOptions={crmToolOptions}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tools catalog — the canonical capabilities the AI can use. Read-only:
          skills decide when a tool is used, never how it works. */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Tools</h3>
          <p className="text-xs text-muted-foreground">
            Built-in capabilities the AI can use. Ambient tools are always available; the rest
            become available through the skills that grant them.
          </p>
        </div>
        <div className="rounded-lg border bg-card divide-y">
          {AGENT_TOOLS.map((tool) => {
            const usedBy = tool.grantable
              ? (skills || []).filter(
                  (skill) => skill.enabled && (skill.tools || []).includes(tool.name)
                ).length
              : 0;
            return (
              <div key={tool.name} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 p-3">
                <div className="min-w-0 flex-1 basis-48 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{tool.label}</span>
                    {tool.grantable ? (
                      <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                        Granted by skills
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
                        Always available
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{tool.description}</p>
                </div>
                {tool.grantable && (
                  <span className="text-xs text-muted-foreground shrink-0 pt-0.5">
                    {usedBy === 0
                      ? 'Not used by any skill'
                      : `Used by ${usedBy} skill${usedBy === 1 ? '' : 's'}`}
                  </span>
                )}
              </div>
            );
          })}
          {mcpToolOptions.map((option) => {
            const usedBy = (skills || []).filter(
              (skill) => skill.enabled && (skill.tools || []).includes(option.name)
            ).length;
            return (
              <div key={option.name} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 p-3">
                <div className="min-w-0 flex-1 basis-48 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{option.label}</span>
                    <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                      MCP · Granted by skills
                    </Badge>
                  </div>
                  {option.description && (
                    <p
                      className="text-xs text-muted-foreground line-clamp-2"
                      title={option.description}
                    >
                      {option.description}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0 pt-0.5">
                  {usedBy === 0
                    ? 'Not used by any skill'
                    : `Used by ${usedBy} skill${usedBy === 1 ? '' : 's'}`}
                </span>
              </div>
            );
          })}
          {crmToolOptions.map((option) => {
            const usedBy = (skills || []).filter(
              (skill) => skill.enabled && (skill.tools || []).includes(option.name)
            ).length;
            return (
              <div key={option.name} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 p-3">
                <div className="min-w-0 flex-1 basis-48 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{option.label}</span>
                    <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                      CRM · Verified visitors
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 pt-0.5">
                  {usedBy === 0
                    ? 'Not used by any skill'
                    : `Used by ${usedBy} skill${usedBy === 1 ? '' : 's'}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
}
